import { safeLocalStorage } from "./safe-storage";
// Sistema de consentimiento de cookies para el sitio público.
//
// El consentimiento se persiste en localStorage como JSON. La key de almacenamiento
// se versiona para poder forzar re-consentimiento si añadimos categorías nuevas
// (sólo hay que subir CONSENT_VERSION).
//
// Reglas:
// - "technical" siempre activas (la lógica de la app no las puede desactivar).
// - "analytics" y "marketing" requieren opt-in explícito.
// - El sitio NO debe cargar Vercel Analytics, Sentry replay ni scripts de
//   marketing antes de leer el consentimiento. Componentes que las consumen
//   leen este estado vía useConsent() y se montan sólo si está concedido.

// 2 (LDT-738): entra el Píxel de Meta en «Marketing». Un «sí» de la versión 1
// se dio cuando esa categoría no cargaba nada: hay que volver a preguntar.
export const CONSENT_VERSION = 2;
export const CONSENT_STORAGE_KEY = "playium_cookie_consent_v1";

export interface ConsentChoice {
  /** Siempre true por definición. Se incluye para que el shape sea simétrico. */
  technical: true;
  analytics: boolean;
  marketing: boolean;
  /** Marca el momento en el que el usuario consintió, para auditoría. */
  decidedAt: string;
  /** Versión del esquema; si subimos categorías, comparamos contra CONSENT_VERSION. */
  version: number;
}

const DEFAULT_OFF: Omit<ConsentChoice, "decidedAt" | "version"> = {
  technical: true,
  analytics: false,
  marketing: false,
};

/** Decisión "rechazar todo" — sólo técnicas. */
export function rejectAllChoice(): ConsentChoice {
  return {
    ...DEFAULT_OFF,
    decidedAt: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
}

/** Decisión "aceptar todo". */
export function acceptAllChoice(): ConsentChoice {
  return {
    technical: true,
    analytics: true,
    marketing: true,
    decidedAt: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
}

/** Decisión personalizada — asegura que technical queda en true. */
export function customChoice(opts: { analytics: boolean; marketing: boolean }): ConsentChoice {
  return {
    technical: true,
    analytics: !!opts.analytics,
    marketing: !!opts.marketing,
    decidedAt: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
}

/** Lee el consentimiento guardado; null = aún no se ha decidido. */
export function readConsent(): ConsentChoice | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = safeLocalStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentChoice>;
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof parsed.version !== "number" ||
      parsed.version !== CONSENT_VERSION
    ) {
      // Versión incompatible: forzamos re-consentimiento.
      return null;
    }
    return {
      technical: true,
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      decidedAt: typeof parsed.decidedAt === "string" ? parsed.decidedAt : new Date().toISOString(),
      version: parsed.version,
    };
  } catch {
    return null;
  }
}

/** Guarda el consentimiento. */
export function writeConsent(choice: ConsentChoice): void {
  if (typeof window === "undefined") return;
  safeLocalStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(choice));
}

/** Borra el consentimiento (útil para testing). */
export function clearConsent(): void {
  if (typeof window === "undefined") return;
  safeLocalStorage.removeItem(CONSENT_STORAGE_KEY);
}
